import { useEffect, useState, useRef } from "react";
import { Bell, CheckCheck, Loader2, AtSign, MessageSquare, Megaphone } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate } from "../lib/api";
import { subscribeWS } from "../lib/ws";
import { toast } from "sonner";

export default function NotificationsBell() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const ref = useRef(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await api.get("/notifications?limit=20");
      setItems(r.data.items || []);
      setUnread(r.data.unread || 0);
      setNextCursor(r.data.next_cursor || null);
    } finally { setLoading(false); }
  };

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const r = await api.get(`/notifications?limit=20&cursor=${encodeURIComponent(nextCursor)}`);
      setItems((prev) => [...prev, ...(r.data.items || [])]);
      setNextCursor(r.data.next_cursor || null);
    } finally { setLoadingMore(false); }
  };

  useEffect(() => {
    fetchAll();
    const unsub = subscribeWS((msg) => {
      if (msg.type === "notification" && msg.payload) {
        setItems((prev) => [msg.payload, ...prev]);
        setUnread((c) => c + 1);
        toast.message(msg.payload.title, { description: msg.payload.message });
      }
    });
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => { unsub(); document.removeEventListener("mousedown", onClick); };
  }, []);

  const markRead = async (n) => {
    if (!n.read) {
      await api.patch(`/notifications/${n.id}/read`).catch(() => {});
      setItems((prev) => prev.map((x) => x.id === n.id ? { ...x, read: true } : x));
      setUnread((c) => Math.max(0, c - 1));
    }
    if (n.link) { setOpen(false); navigate(n.link); }
  };

  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    setUnread(0);
  };

  return (
    <div ref={ref} className="relative">
      <button onClick={() => { const next = !open; setOpen(next); if (next) fetchAll(); }}
        className="relative p-2 text-slate-500 hover:text-btp-navy" data-testid="notifications-trigger">
        <Bell size={18} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-btp-terra text-white text-[10px] font-bold flex items-center justify-center" data-testid="notifications-badge">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-white border border-slate-200 rounded-md shadow-xl z-30 overflow-hidden" data-testid="notifications-dropdown">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h3 className="font-heading font-bold text-sm">Notifications</h3>
              <div className="text-xs text-slate-500">{unread} non lue{unread !== 1 ? "s" : ""}</div>
            </div>
            <button onClick={markAll} disabled={unread === 0}
              className="text-xs text-btp-navy font-semibold disabled:text-slate-400 flex items-center gap-1"
              data-testid="notifications-mark-all">
              <CheckCheck size={13} /> Tout marquer lu
            </button>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {loading && <div className="p-6 text-center text-slate-400"><Loader2 size={18} className="animate-spin mx-auto" /></div>}
            {!loading && items.length === 0 && (
              <div className="p-8 text-center">
                <Bell className="w-10 h-10 mx-auto text-slate-200 mb-2" />
                <p className="text-sm text-slate-500">Aucune notification</p>
              </div>
            )}
            {items.map((n) => (
              <button key={n.id} onClick={() => markRead(n)}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 flex items-start gap-3 ${!n.read ? "bg-btp-terra/5" : ""}`}
                data-testid={`notif-${n.id}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${n.type === "mention" ? "bg-btp-terra/10 text-btp-terra" : n.type === "comment" ? "bg-blue-50 text-blue-600" : "bg-slate-100 text-slate-600"}`}>
                  {n.type === "mention" ? <AtSign size={14} /> : n.type === "comment" ? <MessageSquare size={14} /> : <Megaphone size={14} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-semibold text-slate-900 leading-snug">{n.title}</div>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-btp-terra flex-shrink-0 mt-1.5"></span>}
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{n.message}</div>
                  <div className="text-[10px] font-mono text-slate-400 mt-1">{fmtDate(n.created_at)}</div>
                </div>
              </button>
            ))}
            {nextCursor && (
              <button onClick={loadMore} disabled={loadingMore}
                className="w-full px-4 py-3 text-sm font-semibold text-btp-navy hover:bg-slate-50 flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="notifications-load-more">
                {loadingMore ? <Loader2 size={13} className="animate-spin" /> : null}
                {loadingMore ? "Chargement…" : "Charger plus"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
